// Queue pause control — admin kill-switch for the gl_jobs queue.
// Pausable today: the two jobs that write to NetSuite. Must mirror the
// backend allowlist (`_PAUSABLE` in api/src/api/v1/queue.py).

import { getInit, type PollOptions } from './poll';

export const PRICE_PUSH_JOB_TYPE = 'push_price_to_ns';
export const DISABLE_ITEM_JOB_TYPE = 'disable_item_in_ns';

export interface PausableJobMeta {
  jobType: string;
  title: string;
  /** Short noun used in the global banner, e.g. "price updates". */
  bannerLabel: string;
  description: string;
  confirmMessage: string;
}

export const PAUSABLE_JOBS: PausableJobMeta[] = [
  {
    jobType: PRICE_PUSH_JOB_TYPE,
    title: 'Price updates → NetSuite',
    bannerLabel: 'price updates',
    description:
      'While paused, approved price checks stay queued and are not pushed to NetSuite (→ Celigo → Magento). They drain automatically once resumed.',
    confirmMessage:
      'Pause price updates?\n\nApproved price checks will keep queueing but will NOT be pushed to NetSuite until you resume.',
  },
  {
    jobType: DISABLE_ITEM_JOB_TYPE,
    title: 'Item disables → NetSuite',
    bannerLabel: 'item disables',
    description:
      'While paused, items marked Disable stay queued and are not inactivated in NetSuite (they remain visible on the storefront). They drain automatically once resumed.',
    confirmMessage:
      'Pause item disables?\n\nItems marked Disable will keep queueing but will NOT be inactivated in NetSuite until you resume.',
  },
];

export interface QueuePause {
  job_type: string;
  is_paused: boolean;
  reason: string | null;
  updated_by_email: string | null;
  updated_at: string | null;
}

export async function fetchQueuePause(opts: PollOptions = {}): Promise<QueuePause[]> {
  const res = await fetch('/api/v1/queue/pause', getInit(opts));
  if (!res.ok) throw new Error(`Queue pause fetch failed: ${res.status}`);
  return res.json() as Promise<QueuePause[]>;
}

export async function setQueuePause(
  jobType: string,
  isPaused: boolean,
  reason?: string | null,
): Promise<QueuePause> {
  const res = await fetch(`/api/v1/queue/pause/${encodeURIComponent(jobType)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_paused: isPaused, reason: reason ?? null }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Update failed: ${res.status} ${detail}`);
  }
  return res.json() as Promise<QueuePause>;
}
